import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { stringifySearch } from '@src/utils/urlHelpers';

/**
 * PageNumber Component
 *
 * Individual page number link in pagination navigation.
 * Handles URL navigation and onClick callbacks for page changes.
 * Automatically updates search parameters with the page number.
 *
 * ## Features
 * - Accessible navigation with ARIA labels
 * - Visual indication for current page
 * - URL parameter management via search params
 * - Custom onClick callback support
 * - Screen reader friendly with hidden text
 *
 * ## Migration from Class Component
 * - Changed from `React.PureComponent` to functional component
 * - Converted `handleClick` method to `useCallback` hook
 * - Optimized with `React.memo` for performance
 * - Maintained all existing functionality and API
 * - Added comprehensive JSDoc documentation
 *
 * @component
 * @param {boolean} [isCurrentPage=false] - Whether this is the current page
 * @param {number} number - Page number to display and navigate to
 * @param {Object} searchParams - URL search parameters object
 * @param {string} pathname - Current URL pathname
 * @param {Function} onClick - Click handler for page navigation
 * @returns {React.ReactElement} - Page number link component
 *
 * @example
 * // Current page (page 2)
 * <PageNumber
 *   isCurrentPage={true}
 *   number={2}
 *   searchParams={{ category: 'products' }}
 *   pathname="/products"
 *   onClick={handlePageClick}
 * />
 *
 * @example
 * // Regular page (page 3)
 * <PageNumber
 *   isCurrentPage={false}
 *   number={3}
 *   searchParams={{ category: 'products' }}
 *   pathname="/products"
 *   onClick={handlePageClick}
 * />
 *
 * @example
 * // Page 1 (special case - removes page param from URL)
 * <PageNumber
 *   isCurrentPage={false}
 *   number={1}
 *   searchParams={{ category: 'products' }}
 *   pathname="/products"
 *   onClick={handlePageClick}
 * />
 */
const PageNumber = ({
    isCurrentPage = false,
    number,
    searchParams,
    pathname,
    onClick,
}) => {
    /**
     * Handle click on page number
     * Updates URL search parameters and calls onClick callback
     */
    const handleClick = React.useCallback(() => {
        onClick(number);
    }, [number, onClick]);

    // Create updated search parameters for this page
    // Note: We avoid mutating the original searchParams object
    const updatedSearchParams = { ...(searchParams || {}) };
    updatedSearchParams.page = number;

    const thisClassNames = classNames({
        'ml-pagination-numbers': true,
        'ml-current-page': isCurrentPage,
    });

    const title = isCurrentPage ? `Current Page, Page ${number}` : `Go to Page ${number}`;

    return (
        <Link
            aria-current={isCurrentPage ? 'page' : null}
            title={title}
            aria-label={title}
            className={thisClassNames}
            id={`page_${number}`}
            to={{ pathname, search: stringifySearch(updatedSearchParams) }}
            onClick={handleClick}
        >
            {
                !isCurrentPage && <span className="d-none">page </span>
            }
            {number}
        </Link>
    );
};

PageNumber.propTypes = {
    isCurrentPage: PropTypes.bool,
    number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    searchParams: PropTypes.object,
    pathname: PropTypes.string,
    onClick: PropTypes.func.isRequired,
};

export default React.memo(PageNumber);

